import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TreePine, Banknote, Leaf, ArrowRight, Ruler, Axe, Sprout, Scissors,
  TreeDeciduous, TrendingUp, MapPin, ClipboardList, Lock, UserPlus, Home,
  Percent, Layers, Briefcase,
  Star, Sparkles, GitCompare, Info
} from 'lucide-react';

// Import calculators
import VirkesvolymKalkylator from '../components/calculators/VirkesvolymKalkylator';
import VirkesvardeKalkylator from '../components/calculators/VirkesvardeKalkylator';
import AvverkningsintaktKalkylator from '../components/calculators/AvverkningsintaktKalkylator';
import AvverkningsintaktKalkylatorPremium from '../components/calculators/AvverkningsintaktKalkylatorPremium';
import PlanteringskostnadKalkylator from '../components/calculators/PlanteringskostnadKalkylator';
import RojningskostnadKalkylator from '../components/calculators/RojningskostnadKalkylator';
import GallringsKalkylator from '../components/calculators/GallringsKalkylator';
import GallringsKalkylatorPremium from '../components/calculators/GallringsKalkylatorPremium';
import SkogstillvaxtKalkylator from '../components/calculators/SkogstillvaxtKalkylator';
import MarkvardeKalkylator from '../components/calculators/MarkvardeKalkylator';
import CO2Kalkylator from '../components/calculators/CO2Kalkylator';
import SkogsprojektKalkylator from '../components/calculators/SkogsprojektKalkylator';
import FastighetsvardeKalkylator from '../components/calculators/FastighetsvardeKalkylator';
import PrisPerHektarKalkylator from '../components/calculators/PrisPerHektarKalkylator';
import MarkVirkeVardeKalkylator from '../components/calculators/MarkVirkeVardeKalkylator';
import MaklarProvisionKalkylator from '../components/calculators/MaklarProvisionKalkylator';
import AvanceradKalkylator from '../components/calculators/AvanceradKalkylator';
import ScenarioComparison from '../components/calculators/ScenarioComparison';

// Import config and translations
import { verktygsTranslations, PREMIUM_CALCULATOR_IDS } from '../lib/verktygsTranslations';

// Wartemodus
import { AUTH_ENABLED, SALES_ENABLED } from '../lib/featureFlags';

// Calculator Card Component
const CalculatorCard = ({ calc, category, isPremium, premiumLabel, onSelect }) => (
  <Card 
    className={`cursor-pointer hover:border-forest-300 hover:shadow-md transition-all ${
      isPremium ? 'border-forest-500 ring-2 ring-forest-300 shadow-sm' : ''
    }`}
    onClick={() => onSelect({ ...calc, category })}
    data-testid={`calc-card-${calc.id}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${isPremium ? 'bg-forest-200' : 'bg-forest-50'}`}>
          <calc.icon className="h-5 w-5 text-forest-700" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium text-forest-800">{calc.name}</h3>
            {isPremium && (
              <span className="px-2 py-0.5 text-xs bg-forest-700 text-white rounded-full flex items-center gap-1 font-medium">
                <Sparkles className="h-3 w-3" />
                {premiumLabel}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{calc.description}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-forest-400 mt-1" />
      </div>
    </CardContent>
  </Card>
);

// Login Required Screen
const LoginRequiredScreen = ({ content }) => (
  <div className="max-w-2xl mx-auto space-y-8" data-testid="login-required">
    <div className="text-center space-y-4">
      <div className="flex justify-center">
        <div className="p-4 rounded-full bg-forest-100">
          <Lock className="h-12 w-12 text-forest-700" />
        </div>
      </div>
      <h1 className="text-3xl font-bold text-forest-800">{content.loginRequired}</h1>
      <p className="text-lg text-muted-foreground">{content.loginText}</p>
    </div>

    <Card className="bg-forest-50 border-forest-200">
      <CardContent className="p-6">
        <p className="text-forest-700 text-center">{content.seoIntro}</p>
      </CardContent>
    </Card>

    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <Link to="/register">
        <Button size="lg" className="w-full sm:w-auto bg-forest-700 hover:bg-forest-800">
          <UserPlus className="mr-2 h-5 w-5" />
          {content.createAccount}
        </Button>
      </Link>
      <Link to="/login">
        <Button size="lg" variant="outline" className="w-full sm:w-auto">
          {content.login}
        </Button>
      </Link>
    </div>

    {/* Preview of calculators */}
    <div className="space-y-4 opacity-50 pointer-events-none">
      <h2 className="text-xl font-semibold text-center text-forest-700">{content.pageTitle}</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[TreePine, Banknote, Axe, TrendingUp, Leaf].map((Icon, i) => (
          <div key={i} className="p-4 rounded-lg bg-white border text-center">
            <Icon className="h-8 w-8 mx-auto text-forest-400 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Guest Mode Banner - shown to non-logged-in users
const GuestModeBanner = ({ content, calcParam }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-800 font-medium">{content.guestModeTitle || 'Testa utan inloggning'}</p>
          <p className="text-amber-700 text-sm">
            {AUTH_ENABLED
              ? (content.guestModeText || 'Du kan testa kalkylatorn direkt. Logga in för att spara och jämföra dina resultat.')
              : 'Du kan testa kalkylatorn direkt.'}
          </p>
        </div>
      </div>
      {AUTH_ENABLED && (
        <div className="flex gap-2 flex-shrink-0">
          <Link to={`/register${calcParam ? `?redirect=${encodeURIComponent(`/verktyg?calc=${calcParam}`)}` : ''}`} data-testid="guest-banner-register-link">
            <Button size="sm" className="bg-forest-700 hover:bg-forest-800">
              <UserPlus className="mr-1 h-4 w-4" />
              {content.createAccount || 'Skapa konto'}
            </Button>
          </Link>
          <Link to={`/login${calcParam ? `?redirect=${encodeURIComponent(`/verktyg?calc=${calcParam}`)}` : ''}`} data-testid="guest-banner-login-link">
            <Button size="sm" variant="outline">
              {content.login || 'Logga in'}
            </Button>
          </Link>
        </div>
      )}
    </div>
  </div>
);

// User Type Filter removed (Feb 2026) — there is now only one target group:
// "Skogsägare på distans". The previous multi-role filter is no longer needed.

// Build categories config based on translations
const buildCategories = (c) => ({
  virke: {
    title: c.categories.virke.title,
    description: c.categories.virke.description,
    icon: Ruler,
    calculators: [
      { id: 'virkesvolym', name: c.calculators.virkesvolym.name, description: c.calculators.virkesvolym.description, icon: TreePine, component: VirkesvolymKalkylator, featured: true },
      { id: 'virkesvarde', name: c.calculators.virkesvarde.name, description: c.calculators.virkesvarde.description, icon: Banknote, component: VirkesvardeKalkylator, featured: true },
      { id: 'avverkning', name: c.calculators.avverkning.name, description: c.calculators.avverkning.description, icon: Axe, component: AvverkningsintaktKalkylatorPremium, featured: true, hasPremium: true }
    ]
  },
  planering: {
    title: c.categories.planering.title,
    description: c.categories.planering.description,
    icon: TreeDeciduous,
    calculators: [
      { id: 'plantering', name: c.calculators.plantering.name, description: c.calculators.plantering.description, icon: Sprout, component: PlanteringskostnadKalkylator },
      { id: 'rojning', name: c.calculators.rojning.name, description: c.calculators.rojning.description, icon: Scissors, component: RojningskostnadKalkylator },
      { id: 'gallring', name: c.calculators.gallring.name, description: c.calculators.gallring.description, icon: TreeDeciduous, component: GallringsKalkylatorPremium, hasPremium: true },
      { id: 'tillvaxt', name: c.calculators.tillvaxt.name, description: c.calculators.tillvaxt.description, icon: TrendingUp, component: SkogstillvaxtKalkylator, featured: true }
    ]
  },
  ekonomi: {
    title: c.categories.ekonomi.title,
    description: c.categories.ekonomi.description,
    icon: Banknote,
    calculators: [
      { id: 'markvarde', name: c.calculators.markvarde.name, description: c.calculators.markvarde.description, icon: MapPin, component: MarkvardeKalkylator },
      { id: 'projekt', name: c.calculators.projekt.name, description: c.calculators.projekt.description, icon: ClipboardList, component: SkogsprojektKalkylator }
    ]
  },
  klimat: {
    title: c.categories.klimat.title,
    description: c.categories.klimat.description,
    icon: Leaf,
    calculators: [
      { id: 'co2', name: c.calculators.co2.name, description: c.calculators.co2.description, icon: Leaf, component: CO2Kalkylator, featured: true }
    ]
  },
  maklare: {
    title: c.categories.maklare.title,
    description: c.categories.maklare.description,
    icon: Briefcase,
    calculators: [
      { id: 'fastighetsvarde', name: c.calculators.fastighetsvarde.name, description: c.calculators.fastighetsvarde.description, icon: Home, component: FastighetsvardeKalkylator, featured: true },
      { id: 'prisPerHektar', name: c.calculators.prisPerHektar.name, description: c.calculators.prisPerHektar.description, icon: Ruler, component: PrisPerHektarKalkylator },
      { id: 'markVirke', name: c.calculators.markVirke.name, description: c.calculators.markVirke.description, icon: Layers, component: MarkVirkeVardeKalkylator },
      { id: 'provision', name: c.calculators.provision.name, description: c.calculators.provision.description, icon: Percent, component: MaklarProvisionKalkylator }
    ]
  },
  avancerat: {
    title: c.categories.avancerat?.title || 'Avancerat',
    description: c.categories.avancerat?.description || 'Advanced tools',
    icon: Sparkles,
    calculators: [
      { id: 'avancerad', name: c.calculators.avancerad?.name || 'Avancerad skogskalkyl', description: c.calculators.avancerad?.description || 'Complete analysis', icon: Sparkles, component: AvanceradKalkylator, featured: true },
      { id: 'jamfor', name: c.calculators.jamfor?.name || 'Jämför scenarier', description: c.calculators.jamfor?.description || 'Compare scenarios', icon: GitCompare, component: ScenarioComparison }
    ]
  }
});

const Verktyg = () => {
  const { isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('virke');
  const [selectedCalculator, setSelectedCalculator] = useState(null);

  const c = verktygsTranslations[language] || verktygsTranslations.sv;
  const categories = useMemo(() => buildCategories(c), [c]);

  // Check for open_case URL parameter (from Mina ärenden) or direct calc link
  // Works for BOTH logged-in and non-logged-in users
  useEffect(() => {
    const openCaseId = searchParams.get('open_case');
    let calcParam = searchParams.get('calc');

    // Fallback: if returning from Stripe (success or cancelled) without `calc`,
    // try to infer it from the case so the right calculator auto-opens and
    // the polling/unlock logic actually runs.
    const beslutslageSuccess = searchParams.get('beslutslage_success') === 'true';
    const beslutslageCancelled = searchParams.get('beslutslage_cancelled') === 'true';
    const caseId = searchParams.get('case_id');

    if (!calcParam && (beslutslageSuccess || beslutslageCancelled) && caseId && isAuthenticated) {
      calcParam = 'fastighetsvarde';
    }
    
    if (calcParam) {
      const calcMappings = {
        'fastighetsvarde': { tab: 'maklare', id: 'fastighetsvarde' },
        'virkesvarde': { tab: 'virke', id: 'virkesvarde' },
        'virkesvolym': { tab: 'virke', id: 'virkesvolym' },
        'tillvaxt': { tab: 'planering', id: 'tillvaxt' },
        'co2': { tab: 'klimat', id: 'co2' },
        'avverkning': { tab: 'virke', id: 'avverkning' },
        'plantering': { tab: 'planering', id: 'plantering' },
        'rojning': { tab: 'planering', id: 'rojning' },
        'gallring': { tab: 'planering', id: 'gallring' },
        'markvarde': { tab: 'ekonomi', id: 'markvarde' },
        'projekt': { tab: 'ekonomi', id: 'projekt' },
        'prisPerHektar': { tab: 'maklare', id: 'prisPerHektar' },
        'markVirke': { tab: 'maklare', id: 'markVirke' },
        'provision': { tab: 'maklare', id: 'provision' },
        'avancerad': { tab: 'avancerat', id: 'avancerad' },
        'jamfor': { tab: 'avancerat', id: 'jamfor' }
      };
      
      const mapping = calcMappings[calcParam];
      if (mapping) {
        const category = categories[mapping.tab];
        const calculator = category?.calculators.find(c => c.id === mapping.id);
        if (calculator) {
          setActiveTab(mapping.tab);
          setSelectedCalculator({ ...calculator, category: mapping.tab });
        }
      }
    } else if (openCaseId && isAuthenticated) {
      const fastighetsvärdeCalc = categories.maklare.calculators.find(
        calc => calc.id === 'fastighetsvarde'
      );
      if (fastighetsvärdeCalc) {
        setActiveTab('maklare');
        setSelectedCalculator({ ...fastighetsvärdeCalc, category: 'maklare' });
      }
    }
  }, [searchParams, isAuthenticated, categories]);

  const isPremium = (calcId) => PREMIUM_CALCULATOR_IDS.includes(calcId);

  // Get calc param for redirect after login
  const calcParam = searchParams.get('calc');

  // Detect anonymous user landing on email link (open_case) or Stripe return.
  // Show a clear, friendly login prompt instead of silently dropping them on
  // the picker page.
  const openCaseId = searchParams.get('open_case');
  const beslutslageSuccess = searchParams.get('beslutslage_success') === 'true';
  const beslutslageCancelled = searchParams.get('beslutslage_cancelled') === 'true';
  const needsLoginForCase = !isAuthenticated && (openCaseId || beslutslageSuccess || beslutslageCancelled);

  return (
    <div className="space-y-8" data-testid="verktyg-page">
      {/* Anonymous user opened an email link or returned from Stripe — guide them to login */}
      {needsLoginForCase && (
        <Card className="border-2 border-forest-300 bg-forest-50" data-testid="needs-login-banner">
          <CardContent className="py-5 space-y-3">
            <div className="flex items-start gap-3">
              <Lock className="h-6 w-6 text-forest-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-forest-800 text-lg">
                  {beslutslageSuccess
                    ? 'Logga in för att se ditt beslutsbrev'
                    : 'Logga in för att öppna ditt ärende'}
                </h3>
                <p className="text-sm text-forest-700 mt-1">
                  {beslutslageSuccess
                    ? 'Tack för ditt köp! Logga in med samma e-postadress som du angav vid köpet för att se och ladda ner ditt beslutsbrev.'
                    : 'Vi behöver att du loggar in för att kunna visa ditt ärende. Du ser bara dina egna ärenden – aldrig någon annans.'}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {AUTH_ENABLED && (
                    <Link
                      to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}${beslutslageSuccess ? '&intent=purchase' : ''}`}
                      data-testid="needs-login-login-btn"
                    >
                      <Button size="sm" className="bg-forest-700 hover:bg-forest-800">
                        Logga in
                      </Button>
                    </Link>
                  )}
                  <a href="mailto:info@hodema.se" className="text-sm text-forest-700 hover:underline self-center" data-testid="needs-login-contact-link">
                    Behöver du hjälp? info@hodema.se
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Guest Mode Banner - shown to non-logged-in users (suppressed when needsLoginForCase is shown to avoid double prompt) */}
      {!isAuthenticated && !needsLoginForCase && (
        <GuestModeBanner content={c} calcParam={calcParam} />
      )}

      {/* Hero Section */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl md:text-4xl font-bold text-forest-800">{c.pageTitle}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{c.pageSubtitle}</p>
      </div>

      {/* Defaults notice */}
      <p className="text-sm text-muted-foreground text-center">{c.defaultsNote}</p>

      {selectedCalculator ? (
        // Show selected calculator
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCalculator(null)}
            className="text-forest-700"
            data-testid="back-to-all-btn"
          >
            {c.backToAll}
          </Button>
          <selectedCalculator.component />
        </div>
      ) : (
        // Show calculator grid with tabs
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-6 gap-2 h-auto p-1 bg-forest-50">
            {Object.entries(categories).map(([key, cat]) => (
              <TabsTrigger 
                key={key} 
                value={key}
                className="flex items-center gap-2 py-3 data-[state=active]:bg-forest-700 data-[state=active]:text-white"
                data-testid={`tab-${key}`}
              >
                <cat.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{cat.title}</span>
                <span className="sm:hidden">{cat.title.split(' ')[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(categories).map(([key, cat]) => {
            // Within each category, sort so Premium calculators come first
            const sortedCalcs = [...cat.calculators].sort((a, b) => {
              const aPrem = isPremium(a.id) ? 0 : 1;
              const bPrem = isPremium(b.id) ? 0 : 1;
              return aPrem - bPrem;
            });
            return (
              <TabsContent key={key} value={key} className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-forest-100">
                    <cat.icon className="h-6 w-6 text-forest-700" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-forest-800">{cat.title}</h2>
                    <p className="text-sm text-muted-foreground">{cat.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sortedCalcs.map((calc) => (
                    <CalculatorCard 
                      key={calc.id} 
                      calc={calc} 
                      category={key}
                      isPremium={isPremium(calc.id)}
                      premiumLabel={c.premiumBadge}
                      onSelect={setSelectedCalculator}
                    />
                  ))}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {/* SEO Keywords Section */}
      {!selectedCalculator && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg text-forest-700">{c.aboutTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>{c.aboutText1}</p>
            <p>{c.aboutText2}</p>
            <p>{c.aboutText3}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Verktyg;
